import { bindFiltersToggle, clearFilters, recordRecentlyViewed, renderRecentlyViewed, seedLocaleFilter } from "./search-shared";
import { trackSearchInput } from "./analytics";

// Drives the dedicated /search/ page (layouts/_default/search.html) — a normal
// in-flow page, not an overlay, so unlike search.ts there's no position:
// fixed panel, no body-scroll-lock, no visualViewport syncing, and no
// backdrop/outside-click handling to wire up. See BUILD-LOG.md's "Mobile
// search takeover" entries: this page used to be where mobile's header
// search navigated to; mobile now opens an in-place takeover instead
// (search.ts), and this page stays as the direct-load/no-JS destination
// and the URL that takeover shows.
class SearchPage {
    private _shell: HTMLElement | null = null;
    private _recentList: HTMLElement | null = null;
    private _clearFiltersBtn: HTMLButtonElement | null = null;
    private _input: HTMLInputElement | null = null;
    private _placeholder: string | null = null;

    init() {
        this._shell = document.querySelector<HTMLElement>("#search-page-panel");
        if (!this._shell) {
            return;
        }
        const shell = this._shell;

        this._recentList = document.querySelector<HTMLElement>("#search-page-recent-list");
        this._clearFiltersBtn = document.querySelector<HTMLButtonElement>("#search-page-clear-filters");

        recordRecentlyViewed();
        renderRecentlyViewed(this._recentList);

        this._clearFiltersBtn?.addEventListener("click", () => clearFilters(shell, !!shell.dataset.seedLocale));
        bindFiltersToggle(shell);

        // ?locale=<display name> — set by the header's decoy trigger
        // (body-header.html) when the page it was tapped from is
        // locale-scoped. Hugo can't know the query string at build time, so
        // this page has no server-rendered $currentLocaleDisplay the way a
        // real /nyc/ page does — both the filter seed and the header's own
        // locale-switcher label (otherwise stuck on its build-time default,
        // "All Locales") need to read it from the URL instead.
        const localeName = new URLSearchParams(window.location.search).get("locale") ?? undefined;
        if (localeName) {
            // Same wording as the header pill on the page this came from.
            this._placeholder = `Search ${localeName} reviews…`;
            // "Browse NYC" is the homepage bar's shortcut into a locale;
            // already scoped to one, it reads as a second, different scope.
            document.querySelectorAll<HTMLElement>(".facet-shortcut").forEach((link) => {
                link.hidden = true;
            });
        }
        this.seedLocaleFromQuery(localeName);
        this.syncLocaleSwitcherLabel(localeName);
        this.bindInput();
    }

    // <pagefind-input> renders its own native <input> into the light DOM
    // once pagefind-component-ui.js upgrades the element — see search.ts's
    // bindInput() for why the MutationObserver fallback exists.
    private bindInput() {
        if (!this._shell) {
            return;
        }

        const tryBind = (): boolean => {
            const input = this._shell?.querySelector<HTMLInputElement>("pagefind-input input");
            if (!input) {
                return false;
            }

            this._input = input;
            if (this._placeholder) {
                input.placeholder = this._placeholder;
            }
            input.addEventListener("input", () => this.syncQueryState());
            trackSearchInput(input, "search_page");
            this.syncQueryState();
            // The whole point of landing here is to type a query. Focusing
            // on load brings the keyboard up on desktop and Android; iOS
            // ignores a focus() that isn't made inside a user gesture, which
            // is why mobile no longer navigates here from the header (see
            // search.ts's openTakeover()) — this page is now reached on
            // mobile only by a direct link, a reload, or without JS. The
            // input is 16px (search.scss) so focusing it never triggers
            // iOS's auto-zoom.
            input.focus();
            return true;
        };

        if (tryBind()) {
            return;
        }

        const observer = new MutationObserver(() => {
            if (tryBind()) {
                observer.disconnect();
            }
        });
        observer.observe(this._shell, { childList: true, subtree: true });
    }

    // The shell must fully hide the summary/filter/results columns on an
    // empty query rather than show them with a stale count or nothing in
    // them — data-query drives that in search.scss.
    private syncQueryState() {
        if (!this._input || !this._shell) {
            return;
        }

        this._shell.dataset.query = this._input.value.trim() ? "active" : "empty";
    }

    // Mirrors the data-seed-locale attribute the header's own overlay panel
    // gets server-rendered — this page has to set it itself from the URL
    // (see init()'s comment on why).
    private seedLocaleFromQuery(localeName: string | undefined) {
        if (!this._shell) {
            return;
        }

        if (localeName) {
            this._shell.dataset.seedLocale = localeName;
        }

        seedLocaleFilter(this._shell, localeName, () => {
            // Nothing to flip here — seedLocaleFilter only needs to run
            // once per page load, and this page never reopens.
        });
    }

    // #locale-btn and .m-filterbar's own locale-switch-trigger
    // (body-header.html) both render "All Locales" at build time from
    // $currentLocaleDisplay, which is always empty on this page (no real
    // .Page.Section — see body-header.html's own comment on why) — so
    // without this it stays "All Locales" even when this page was reached
    // from a locale-scoped page via ?locale=. Both need updating, not just
    // one: header.scss shows #locale-btn only at ≥700px or when body has
    // .locale-active (which this page never gets, same reason as above) —
    // below 700px it's the .m-filterbar button that's actually visible,
    // confirmed live (the "fix" only touching #locale-btn silently updated
    // an invisible element on mobile, the exact breakpoint this page is
    // for). Only touches each button's own text node, not its caret <span>.
    private syncLocaleSwitcherLabel(localeName: string | undefined) {
        if (!localeName) {
            return;
        }

        document.querySelectorAll<HTMLElement>("#locale-btn, .m-filterbar .locale-switch-trigger").forEach((btn) => {
            const textNode = Array.from(btn.childNodes).find((node) => node.nodeType === Node.TEXT_NODE);
            if (textNode) {
                textNode.textContent = `${localeName} `;
            }
        });
    }
}

export default new SearchPage();
